import React from 'react';
import { Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, Button } from '@klonup/design-system';
import { useAuth } from '@auth/presentation/hooks/useAuth';
import { useAuthStore } from '@auth/presentation/store/authStore';
import { orgPaths } from '@shared/routes/appPaths';

/**
 * Pantalla que se muestra cuando el usuario está desactivado.
 * Si el usuario está activo, redirige a su ruta según rol.
 */
const InactiveUserPage: React.FC = () => {
  const { logout } = useAuth();
  const user = useAuthStore((s) => s.user);
  const { t } = useTranslation();

  // Usuario activo que navega directamente aquí — redirigir
  if (user?.status !== 'inactive') {
    if (user?.role === 'superadmin') {
      return <Navigate to="/admin/organizations" replace />;
    }
    if (user?.organizationSlug) {
      return <Navigate to={orgPaths.myOrganization(user.organizationSlug)} replace />;
    }
    return <Navigate to="/auth/login" replace />;
  }

  const handleLogout = () => {
    logout();
  };

  return (
    <div className="min-h-screen bg-klonup-light flex items-center justify-center p-4">
      <Card className="max-w-md w-full text-center">
        <div className="p-8 flex flex-col items-center gap-6">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>

          <div data-testid="inactive-message" className="flex flex-col gap-2">
            <h1 className="text-xl font-bold text-gray-900">
              {t('inactive.title', 'Cuenta desactivada')}
            </h1>
            <p className="text-gray-500 text-sm">
              {t('inactive.message', 'Tu usuario ha sido desactivado. Contacta con el administrador de tu organización para recuperar el acceso.')}
            </p>
            <p className="text-gray-500 text-sm">
              {t('inactive.contact', 'Si necesitas ayuda adicional, puedes contactarnos en')}{' '}
              <a
                href="mailto:help@klonup.com"
                className="text-klonup-primary hover:underline"
              >
                help@klonup.com
              </a>
            </p>
          </div>

          <Button
            data-testid="logout-button"
            variant="gradient"
            onClick={handleLogout}
          >
            {t('inactive.logout', 'Cerrar sesión')}
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default InactiveUserPage;
